export interface ParseResult {
  date: string;           // YYYY-MM-DD
  startTime: string | null; // HH:mm
  endTime: string | null;   // HH:mm
  suggestedTitle: string;
  isAllDay: boolean;
}

export function parseEventText(text: string, referenceDate: Date = new Date()): ParseResult {
  const ref = new Date(referenceDate);
  ref.setHours(0, 0, 0, 0);

  // Working copy — we'll remove matched tokens to extract title
  let work = text;
  let detectedDate: Date | null = null;
  let startHour: number | null = null;
  let startMinute = 0;
  let endHour: number | null = null;
  let endMinute = 0;

  // Remove a substring from work by replacing with a space
  function strip(s: string) {
    const idx = work.indexOf(s);
    if (idx !== -1) work = work.slice(0, idx) + ' ' + work.slice(idx + s.length);
  }
  function stripMatch(m: RegExpMatchArray) {
    work = work.replace(m[0], ' ');
  }

  // ── DATE ──────────────────────────────────────────────────────

  // N월 M일
  const mdMatch = work.match(/(\d{1,2})월\s*(\d{1,2})일/);
  if (mdMatch) {
    const month = parseInt(mdMatch[1]) - 1;
    const day = parseInt(mdMatch[2]);
    const d = new Date(ref.getFullYear(), month, day);
    if (d < ref) d.setFullYear(ref.getFullYear() + 1);
    detectedDate = d;
    stripMatch(mdMatch);
  }

  // Relative keywords
  if (!detectedDate) {
    const relatives: [string, number][] = [
      ['글피', 3], ['모레', 2], ['내일', 1], ['오늘', 0], ['어제', -1],
    ];
    for (const [kw, n] of relatives) {
      if (work.includes(kw)) {
        detectedDate = shiftDays(ref, n);
        strip(kw);
        break;
      }
    }
  }

  // N일 후/뒤
  if (!detectedDate) {
    const m = work.match(/(\d+)\s*일\s*(?:후|뒤)/);
    if (m) { detectedDate = shiftDays(ref, parseInt(m[1])); stripMatch(m); }
  }

  // N주 후/뒤
  if (!detectedDate) {
    const m = work.match(/(\d+)\s*주\s*(?:후|뒤)/);
    if (m) { detectedDate = shiftDays(ref, parseInt(m[1]) * 7); stripMatch(m); }
  }

  // (이번주|다음주|지난주)? + 요일
  if (!detectedDate) {
    const DAYS = ['일', '월', '화', '수', '목', '금', '토'];
    const m = work.match(/(이번\s*주|다음\s*주|지난\s*주)?\s*(일|월|화|수|목|금|토)요일/);
    if (m) {
      const mod = m[1] || '';
      const target = DAYS.indexOf(m[2]);
      const cur = ref.getDay();
      let offset: number;
      if (mod.includes('다음')) {
        offset = ((target - cur + 7) % 7) || 7;
        offset += 7;
      } else if (mod.includes('지난')) {
        const d = (cur - target + 7) % 7;
        offset = -(d || 7);
      } else {
        offset = (target - cur + 7) % 7;
        if (offset === 0 && !mod.includes('이번')) offset = 7;
      }
      detectedDate = shiftDays(ref, offset);
      stripMatch(m);
    }
  }

  if (!detectedDate) detectedDate = new Date(ref);

  // ── TIME ──────────────────────────────────────────────────────
  // (date tokens stripped from work, so N월/N일 won't confuse time parser)

  // 오전/오후 N시 (반|M분)?
  const ampm = work.match(/(오전|오후)\s*(\d{1,2})시\s*(?:(\d{1,2})분|(반))?/);
  if (ampm) {
    let h = parseInt(ampm[2]);
    const min = ampm[4] === '반' ? 30 : parseInt(ampm[3] || '0');
    if (ampm[1] === '오후' && h < 12) h += 12;
    if (ampm[1] === '오전' && h === 12) h = 0;
    startHour = h; startMinute = min;
    stripMatch(ampm);
  }

  // Standalone N시 (반|M분)?  — must not be followed by 간 (시간)
  if (startHour === null) {
    const m = work.match(/(\d{1,2})시\s*(?:(\d{1,2})분|(반))?(?!\s*간)/);
    if (m) {
      let h = parseInt(m[1]);
      const min = m[3] === '반' ? 30 : parseInt(m[2] || '0');
      if (h >= 1 && h <= 7) h += 12; // afternoon heuristic
      startHour = h; startMinute = min;
      stripMatch(m);
    }
  }

  // Special keywords
  if (startHour === null) {
    const specials: [string, number][] = [
      ['새벽', 4], ['아침', 8], ['점심', 12], ['저녁', 18], ['밤', 21],
    ];
    for (const [kw, h] of specials) {
      if (work.includes(kw)) { startHour = h; strip(kw); break; }
    }
  }

  // N시간 → end time
  if (startHour !== null) {
    const m = work.match(/(\d+)\s*시간/);
    if (m) {
      const dur = parseInt(m[1]);
      endHour = Math.min(23, startHour + dur);
      endMinute = startMinute;
      stripMatch(m);
    }
  }

  // ── TITLE EXTRACTION ──────────────────────────────────────────
  const title = work
    .replace(/\s*[에서에게로부터까지]\s*/g, ' ')
    .replace(/\s*(?:에|을|를|이|가|은|는|도|으로|로)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const startTimeStr = startHour !== null
    ? `${pad(startHour)}:${pad(startMinute)}`
    : null;
  const endTimeStr = endHour !== null
    ? `${pad(endHour)}:${pad(endMinute)}`
    : null;

  return {
    date: dateToString(detectedDate),
    startTime: startTimeStr,
    endTime: endTimeStr,
    suggestedTitle: title || text.trim(),
    isAllDay: startHour === null,
  };
}

function shiftDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function dateToString(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
